import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CurrencyContext = createContext(null);

const DEFAULT_CURRENCIES = [
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
];

export const CurrencyProvider = ({ children }) => {
  const { user } = useAuth();
  const [rates, setRates] = useState({ EUR: 1, USD: 1.08, NGN: 1750 });
  const [currencies, setCurrencies] = useState(DEFAULT_CURRENCIES);
  const [currency, setCurrencyState] = useState(() => localStorage.getItem("xm_currency") || "EUR");

  useEffect(() => {
    api.get("/currency/rates").then(({ data }) => {
      if (data.rates) setRates(data.rates);
      if (data.currencies?.length) setCurrencies(data.currencies);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.currency && ["EUR", "USD", "NGN"].includes(user.currency)) {
      setCurrencyState(user.currency);
      localStorage.setItem("xm_currency", user.currency);
    }
  }, [user]);

  const setCurrency = useCallback((c) => {
    setCurrencyState(c);
    localStorage.setItem("xm_currency", c);
    api.put("/me/settings", { currency: c }).catch(() => {});
  }, []);

  const symbol = useMemo(
    () => currencies.find((x) => x.code === currency)?.symbol || "€",
    [currencies, currency]
  );

  const convert = useCallback((eur) => (Number(eur) || 0) * (rates[currency] || 1), [rates, currency]);

  const format = useCallback((eur) => {
    const val = convert(eur);
    if (currency === "NGN") return `${symbol}${Math.round(val).toLocaleString()}`;
    return `${symbol}${val.toFixed(2)}`;
  }, [convert, currency, symbol]);

  const value = useMemo(
    () => ({ currency, setCurrency, currencies, rates, symbol, format, convert }),
    [currency, setCurrency, currencies, rates, symbol, format, convert]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => useContext(CurrencyContext) || {
  currency: "EUR", symbol: "€", format: (e) => `€${(Number(e) || 0).toFixed(2)}`, setCurrency: () => {}, currencies: DEFAULT_CURRENCIES,
};
